import React, { lazy, Suspense } from "react";
import { useLocation, useParams, BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/index.css";
import "@/mobile.css";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
const CommandCenter = lazy(() => import("@/pages/CommandCenter"));
const Leads = lazy(() => import("@/pages/Leads"));
const AppLeads = lazy(() => import("@/pages/AppLeads"));
const Students = lazy(() => import("@/pages/Students"));
const Counselling = lazy(() => import("@/pages/Counselling"));
const Finance = lazy(() => import("@/pages/Finance"));
const Attendance = lazy(() => import("@/pages/Attendance"));
const Approvals = lazy(() => import("@/pages/Approvals"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const Staff = lazy(() => import("@/pages/Staff"));
const Team = lazy(() => import("@/pages/Team"));
const Settings = lazy(() => import("@/pages/Settings"));
const JoinStaff = lazy(() => import("@/pages/JoinStaff"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
import RankersCRM, { CrmEmbed } from "@/pages/RankersCRM";
const PublicShowBooking = lazy(() => import("@/pages/PublicShowBooking"));
import { canAccess, homeFor, hasCrm } from "@/lib/roles";
import { PermissionsProvider, usePerms } from "@/context/PermissionsContext";

function AccessDenied() {
  return (
    <div className="p-10 text-center" data-testid="access-denied">
      <div className="inline-flex w-14 h-14 rounded-full bg-[#FEE2E2] text-[#C0392B] items-center justify-center text-2xl font-bold mb-3">!</div>
      <h2 className="text-lg font-bold text-[#1A1A2E]">Access denied</h2>
      <p className="text-sm text-[#6B7280] mt-1 max-w-sm mx-auto">Your role doesn't have permission to view this screen. Please contact a Director if you believe you need access.</p>
    </div>
  );
}

function Protected({ children, path }) {
  const { user } = useAuth();
  const { os, loaded } = usePerms();
  if (user === null || (user && !loaded)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF7F3]">
        <div className="w-6 h-6 border-2 border-[#FDEBDD] border-t-[#C2410C] rounded-full animate-spin" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  const denied = path && !canAccess(path, user.role, os);
  return <AppShell>{denied ? <AccessDenied /> : children}</AppShell>;
}

function Home() {
  const { user } = useAuth();
  const { os, loaded } = usePerms();
  if (user && loaded && !canAccess("/", user.role, os)) return <Navigate to={homeFor(user.role)} replace />;
  return <Protected path="/"><CommandCenter /></Protected>;
}

function CrmRoute() {
  const { screen } = useParams();
  const { user } = useAuth();
  if (user && !hasCrm(user.role)) return <AccessDenied />;
  return <CrmEmbed screen={screen || "home"} />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<div data-testid="route-loading" style={{ padding: 32, color: "#9A8F85", fontSize: 13 }}>Loading…</div>}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/join/:centerId" element={<JoinStaff />} />
      <Route path="/book/show/:showId" element={<PublicShowBooking />} />
      <Route path="/crm-standalone" element={<RankersCRM />} />
      <Route path="/crm/tv" element={<CrmEmbed screen="tv" bare />} />
      <Route path="/crm/token-board" element={<CrmEmbed screen="token" bare />} />
      <Route path="/crm/board-tv" element={<CrmEmbed screen="boardtv" bare />} />
      <Route path="/crm" element={<Protected><CrmRoute /></Protected>} />
      <Route path="/crm/:screen" element={<Protected><CrmRoute /></Protected>} />
      <Route path="/" element={<Home />} />
      <Route path="/leads" element={<Protected path="/leads"><Leads /></Protected>} />
      <Route path="/app-leads" element={<Protected path="/app-leads"><AppLeads /></Protected>} />
      <Route path="/students" element={<Protected path="/students"><Students /></Protected>} />
      <Route path="/counselling" element={<Protected path="/counselling"><Counselling /></Protected>} />
      <Route path="/finance" element={<Protected path="/finance"><Finance /></Protected>} />
      <Route path="/attendance" element={<Protected path="/attendance"><Attendance /></Protected>} />
      <Route path="/approvals" element={<Protected path="/approvals"><Approvals /></Protected>} />
      <Route path="/tasks" element={<Protected path="/tasks"><Tasks /></Protected>} />
      <Route path="/staff" element={<Protected path="/staff"><Staff /></Protected>} />
      <Route path="/team" element={<Protected path="/team"><Team /></Protected>} />
      <Route path="/settings" element={<Protected path="/settings"><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PermissionsProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </PermissionsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
